import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";

const CareerTimeline = () => {
  const [selectedMilestone, setSelectedMilestone] = useState(null);

  const timelineMilestones = [
    {
      year: "2024",
      title: "Architecture to Software Engineering and Visual Storytelling",
      category: "Career Transition",
      description:
        "Transitioned from Architecture to pursue passion for software engineering and visual storytelling.",
      details: `After five years in Architecture, Mugambi made the bold decision to follow his passion for visual storytelling. The analytical mindset and attention to detail from Architecture became the foundation of his systematic approach to cinematography and UI/UX.

This transition wasn't just a career change — it was the early foundation of the HYPE philosophy, where technical precision meets creative vision.`,
      achievements: [
        "Completed software engineering certification program",
        "First corporate video project for local events startup",
        "Invested in professional camera equipment",
        "Established systematic workflow processes",
      ],
      image: "/assets/images/hero/denis.jpg",
      icon: "GraduationCap",
    },

    {
      year: "2025",
      title: "HYPE Founded — Accessibility & Innovation",
      category: "Foundation",
      description:
        "HYPE launched to democratize branding, events, and marketing while introducing people-first innovation.",
      details: `HYPE began with a clear belief: high-quality branding, events, and marketing should not be limited to companies with massive budgets. The founders set out to remove industry barriers and democratize creativity for businesses of all sizes.

From day one, HYPE embedded people-first innovation — using technology and insight-led strategy to simplify access to professional marketing, design, and AV solutions.

This defined HYPE’s accessibility-first model: inclusive, results-driven, and built to empower both SMEs and corporates.`,
      achievements: [
        "Defined accessibility-first agency model",
        "Launched branding, events, and AV services",
        "Integrated technology-driven workflows",
        "Simplified client onboarding systems",
        "Built initial SME and corporate client base",
      ],
      image: "/assets/images/hero/about5.png",
      icon: "Sparkles",
    },

    {
      year: "2026",
      title: "Creative + Strategy Leadership",
      category: "Leadership",
      description: "Dual-founder leadership shaped HYPE’s identity and growth.",
      details: `Sumeiya Ahmed AlKizim drove strategic marketing leadership while Denis Mugambi led creative direction and storytelling.

This combined leadership fused brand strategy, data, design, and event execution into one integrated model.`,
      achievements: [
        "Defined bold brand identity",
        "Built multidisciplinary creative teams",
        "Delivered integrated campaigns",
        "Strengthened corporate partnerships",
      ],
      image: "/assets/images/hero/about4.png",
      icon: "Users",
    },

    //   {
    //     year: "2027",
    //     title: "Regional Expansion",
    //     category: "Growth",
    //     description:
    //       "Expanded services across Kenya and East Africa.",
    //     details: `HYPE scaled into regional markets, serving corporates, institutions, and SMEs.

    // The company became known for integrated marketing, events, and AV production under one ecosystem.`,
    //     achievements: [
    //       "Entered East African markets",
    //       "Partnered with regional brands",
    //       "Expanded AV production capacity",
    //       "Strengthened regional presence"
    //     ],
    //     image: "/assets/images/hype/expansion.jpg",
    //     icon: "Globe"
    //   },

    //   {
    //     year: "2028",
    //     title: "Industry Recognition",
    //     category: "Recognition",
    //     description:
    //       "Positioned as a fast-growing integrated marketing agency.",
    //     details: `HYPE gained recognition as a fast-growing creative and events company in the region, known for accessibility and results-driven campaigns.`,
    //     achievements: [
    //       "Recognized as a fast-growing agency",
    //       "Delivered multi-industry campaigns",
    //       "Retained high-value clients",
    //       "Strengthened brand credibility"
    //     ],
    //     image: "/assets/images/hype/recognition.jpg",
    //     icon: "Award"
    //   },

    //   {
    //     year: "2029",
    //     title: "Scaling Impact",
    //     category: "Scale",
    //     description:
    //       "Expanded integrated services and long-term partnerships.",
    //     details: `HYPE scaled its integrated model across strategy, design, advertising, digital marketing, and AV production, focusing on measurable brand impact.`,
    //     achievements: [
    //       "Full integrated marketing ecosystem",
    //       "Corporate & institutional partnerships",
    //       "High-impact regional campaigns",
    //       "Expanded creative workforce"
    //     ],
    //     image: "/assets/images/hype/scale.jpg",
    //     icon: "Rocket"
    //   }
  ];

  const stats = [
    { label: "Years of Experience", value: "2+", icon: "Clock" },
    { label: "Corporate Clients", value: "10+", icon: "Building" },
    { label: "Projects Completed", value: "15+", icon: "Film" },
    // { label: "Industry Awards", value: "15+", icon: "Award" }
  ];

  return (
    <div className="space-y-6  lg:space-y-12">
      {/* Section Header */}
      <div className="text-center max-w-3xl mx-auto">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-primary mb-2 sm:mb-4">
          Career <span className="text-red-600">Timeline</span>
        </h2>
        <p className="text-lg sm:text-xl text-text-secondary">
          A journey of continuous growth, innovation, and dedication to
          excellence in corporate cinematography and visual storytelling.
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-3 gap-2 sm:gap-6 md:gap-8 mb-8">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="bg-white p-4 rounded-xl shadow-subtle text-center hover-lift"
          >
            <div className="w-16 h-16 hidden sm:flex items-center justify-center mx-auto mb-4 text-red-600 rounded-full shadow-subtle
                overflow-hidden">

              <Icon name={stat.icon} size={24} strokeWidth={2} />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-primary mb-2">
              {stat.value}
            </p>
            <p className="text-sm text-text-secondary">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-red-600 hidden sm:block" />


        <div className="space-y-12">
          {timelineMilestones.map((milestone, index) => (
            <div key={index} className="relative">
              {/* Timeline Dot */}
              <div className="absolute left-6 w-4 h-4 bg-accent rounded-full border-4 border-white shadow-subtle hidden sm:block" />

              <div className="lg:ml-20">
                <div className="bg-white rounded-2xl shadow-subtle overflow-hidden hover-lift">
                  {/* Milestone Header */}
                  <div className="p-6 border-b border-border">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 shadow-subtle text-red-600 rounded-xl hidden sm:flex items-center justify-center flex-shrink-0">
                          <Icon
                            name={milestone.icon}
                            size={24}
                            strokeWidth={2}
                          />
                        </div>
                        <div>
                          <div className="flex items-center space-x-3 mb-2">
                            <span className="text-xl sm:text-2xl font-bold text-accent">
                              {milestone.year}
                            </span>
                            <span className="px-3 py-1 bg-accent/10 text-accent text-xs font-semibold rounded-full">
                              {milestone.category}
                            </span>
                          </div>
                          <h3 className="text-lg sm:text-xl font-bold text-primary mb-2">
                            {milestone.title}
                          </h3>
                          <p className="text-sm text-text-secondary">
                            {milestone.description}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() =>
                          setSelectedMilestone(
                            selectedMilestone === index ? null : index,
                          )
                        }
                        className="p-2 hover:bg-surface hover:border-red-600  rounded-lg cinematic-transition shadow-subtle border border-border "
                      >
                        <Icon
                          name={
                            selectedMilestone === index
                              ? "ChevronUp"
                              : "ChevronDown"
                          }
                          size={20}
                          color="var(--color-text-secondary)"
                          strokeWidth={2}
                        />
                      </button>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {selectedMilestone === index && (
                    <div className="p-6 bg-surface/50">
                      <div className="grid lg:grid-cols-3 gap-6">
                        {/* Visual */}
                        <div className="hidden lg:block lg:col-span-1">
                          <div className="aspect-square rounded-xl overflow-hidden shadow-subtle">
                            <Image
                              src={milestone.image}
                              alt={milestone.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>

                        {/* Details & Achievements */}
                        <div className="lg:col-span-2 space-y-6">
                          <div>
                            <h4 className="font-bold text-primary text-base sm:text-lg mb-2 sm:mb-4">
                              Story
                            </h4>
                            <p className="text-text-secondary text-sm sm:text-base leading-relaxed">
                              {milestone.details}
                            </p>
                          </div>

                          <div>
                            <h4 className="font-bold text-primary mb-2 sm:mb-4 flex items-center space-x-2">
                              <Icon
                                className="text-red-600"
                                name="CheckCircle"
                                size={20}
                                strokeWidth={2}
                              />
                              <span>Key Achievements</span>
                            </h4>
                            <ul className="space-y-2">
                              {milestone.achievements.map(
                                (achievement, idx) => (
                                  <li
                                    key={idx}
                                    className="flex items-start space-x-3 text-sm sm:text-base"
                                  >
                                    <Icon
                                      name="ArrowRight"
                                      size={16}
                                      strokeWidth={2}
                                      className="mt-1 flex-shrink-0 text-red-600"
                                    />
                                    <span className="text-text-secondary">
                                      {achievement}
                                    </span>
                                  </li>
                                ),
                              )}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Future Vision */}
      <div className="shadow-subtle max-w-3xl mx-auto  rounded-3xl p-4 md:p-6 text-center">
        <Icon
          name="Telescope"
          size={48}
          color="#e20000"
          className="mx-auto mb-2 sm:mb-4"
          strokeWidth={1.5}
        />
        <h3 className="text-2xl font-bold text-black mb-2 sm:mb-4">
          Looking Forward
        </h3>
        <p className="text-base sm:text-lg text-black/90 max-w-3xl mx-auto">
          "The journey continues. Every project teaches us something new, every
          client challenge pushes us to innovate, and every technological
          advancement opens new creative possibilities. The future of corporate
          cinematography is bright, and we're excited to help shape it."
        </p>
      </div>
    </div>
  );
};

export default CareerTimeline;
